import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, BookOpen, DollarSign } from 'lucide-react';
import apiClient from '../services/api';
import '../styles/MasterForms.css';

const AccountHeads = () => {
  // ✅ Get session data from localStorage (matches your app's pattern)
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      
      const sessionData = JSON.parse(sessionStr);
      // Handle both direct sessionData and nested sessionData.session (same as DashboardHome)
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  const [heads, setHeads] = useState([]);
  const [filteredHeads, setFilteredHeads] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterGroup, setFilterGroup] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  const [formData, setFormData] = useState({
    acheadid: null,
    companyid: companyId || null,  // ✅ Use session company ID
    acheadcode: '',
    acheadname: '',
    acgroupid: '',
    headorder: 0,
    openingbalance: 0.00,
    balancetype: 'Dr',
    statushide: false
  });
  
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  // Account Types
  const accountTypes = ['Asset', 'Liability', 'Equity', 'Income', 'Expense'];

  const fetchGroups = async () => {
    try {
      // ✅ Use session company ID from context
      const response = await apiClient.get('/Acgroups.php', {
        params: {
          companyid: companyId,
          statushide: 0
        }
      });
      
      if (response.data.success) {
        setGroups(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching account groups:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      }
    }
  };

  const fetchHeads = async () => {
    setLoading(true);
    try {
      // ✅ Use session company ID from context
      const response = await apiClient.get('/Acheads.php', {
        params: {
          companyid: companyId
        }
      });
      
      if (response.data.success) {
        setHeads(response.data.data || []);
      } else {
        alert('Failed to fetch account heads: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error fetching account heads:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error fetching account heads. Please check if backend is running.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // ✅ Check if session context exists
    if (!companyId || !yearId) {
      console.warn('No session context found. Company or Year not selected.');
      setLoading(false);
      return;
    }
    
    fetchGroups();
    fetchHeads();
  }, [companyId, yearId]);

  useEffect(() => {
    filterHeads();
  }, [searchTerm, filterType, filterGroup, heads]);

  const filterHeads = () => {
    let filtered = heads;

    if (filterType !== 'all') {
      filtered = filtered.filter(head => head.actype === filterType);
    }

    if (filterGroup !== 'all') {
      filtered = filtered.filter(head => head.acgroupid === parseInt(filterGroup));
    }

    if (searchTerm.trim()) {
      filtered = filtered.filter(head =>
        head.acheadname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        head.acheadcode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        head.acgroupname?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredHeads(filtered);
  };

  const getTypeColor = (type) => {
    const colors = {
      'Asset': '#10b981',
      'Liability': '#ef4444',
      'Equity': '#3b82f6',
      'Income': '#8b5cf6',
      'Expense': '#f59e0b'
    };
    return colors[type] || '#6b7280';
  };

  const formatBalance = (balance, type) => {
    const formatted = parseFloat(balance || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${formatted} ${type}`;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.acheadname.trim()) {
      newErrors.acheadname = 'Account head name is required';
    }
    
    if (!formData.acgroupid) {
      newErrors.acgroupid = 'Account group is required';
    }

    if (formData.openingbalance && isNaN(formData.openingbalance)) {
      newErrors.openingbalance = 'Opening balance must be a number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    
    try {
      // ✅ Use apiClient with session-based company ID
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/Acheads.php', {
        ...formData,
        companyid: companyId,  // ✅ Ensure session company ID is used
        statushide: formData.statushide ? 1 : 0
      });
      
      const data = response.data;
      
      if (data.success) {
        if (!editMode && data.data && data.data.acheadcode) {
          alert(`Account head created successfully!\nHead Code: ${data.data.acheadcode}`);
        } else {
          alert(editMode ? 'Account head updated successfully!' : 'Account head created successfully!');
        }
        setShowModal(false);
        resetForm();
        fetchHeads();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving account head:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error saving account head. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (head) => {
    setFormData({
      acheadid: head.acheadid,
      companyid: head.companyid,
      acheadcode: head.acheadcode,
      acheadname: head.acheadname,
      acgroupid: head.acgroupid,
      headorder: head.headorder || 0,
      openingbalance: head.openingbalance || 0.00,
      balancetype: head.balancetype || 'Dr',
      statushide: head.statushide === 1
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this account head? This cannot be undone if there are transactions.')) {
      return;
    }
    
    try {
      // ✅ Use apiClient for DELETE
      const response = await apiClient.delete('/Acheads.php', {
        data: { acheadid: id }
      });
      
      const data = response.data;
      
      if (data.success) {
        alert('Account head deleted successfully!');
        fetchHeads();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting account head:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error deleting account head. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      acheadid: null,
      companyid: companyId || null,  // ✅ Reset to session company ID
      acheadcode: '',
      acheadname: '',
      acgroupid: '',
      headorder: 0,
      openingbalance: 0.00,
      balancetype: 'Dr',
      statushide: false
    });
    setEditMode(false);
    setErrors({});
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  // ✅ Show message if no session context
  if (!companyId || !yearId) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard to continue.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-title">
          <h2>Account Heads</h2>
          <p className="subtitle">Manage chart of accounts - individual account heads</p>
        </div>
        <button className="btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={20} />
          Add Account Head
        </button>
      </div>

      <div className="filter-section">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by head name, code, or group..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm('')}>
              <X size={18} />
            </button>
          )}
        </div>

        <div className="filter-controls">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            {accountTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          <select
            value={filterGroup}
            onChange={(e) => setFilterGroup(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Groups</option>
            {groups.map(group => (
              <option key={group.acgroupid} value={group.acgroupid}>
                {group.acgroupname}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading account heads...</p>
        </div>
      ) : filteredHeads.length === 0 ? (
        <div className="empty-state">
          <BookOpen size={48} color="#94a3b8" />
          <h3>No Account Heads Found</h3>
          <p>
            {searchTerm || filterType !== 'all' || filterGroup !== 'all'
              ? 'No account heads match your filters. Try adjusting your search.'
              : 'Start by adding your first account head.'}
          </p>
        </div>
      ) : (
        <div className="table-container">
          <table className="master-table">
            <thead>
              <tr>
                <th>Head Code</th>
                <th>Head Name</th>
                <th>Account Group</th>
                <th>Type</th>
                <th>Opening Balance</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredHeads.map((head) => (
                <tr key={head.acheadid}>
                  <td className="font-mono">{head.acheadcode}</td>
                  <td className="font-semibold">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <BookOpen size={16} color="#667eea" />
                      {head.acheadname}
                    </div>
                  </td>
                  <td>{head.acgroupname}</td>
                  <td>
                    <span className="type-badge" style={{
                      background: getTypeColor(head.actype),
                      color: '#fff',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {head.actype}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <DollarSign size={14} color="#6b7280" />
                      <span className="font-mono">
                        {formatBalance(head.openingbalance, head.balancetype)}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${head.statushide ? 'inactive' : 'active'}`}>
                      {head.statushide ? 'Hidden' : 'Active'}
                    </span>
                  </td>
                  <td>{new Date(head.createdat).toLocaleDateString()}</td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(head)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(head.acheadid)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Account Head' : 'Add New Account Head'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              {!editMode && (
                <div style={{
                  background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                  border: '2px solid #3b82f6',
                  borderRadius: '10px',
                  padding: '12px 16px',
                  marginBottom: '20px',
                  display: 'flex',
                  alignments: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '20px' }}>ℹ️</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af' }}>
                    Head code will be auto-generated (e.g., AH001, AH002, etc.)
                  </span>
                </div>
              )}
              
              {editMode && formData.acheadcode && (
                <div className="form-group">
                  <label>Head Code (Auto-generated)</label>
                  <input
                    type="text"
                    value={formData.acheadcode}
                    disabled
                    style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </div>
              )}

              <div className="form-section">
                <h4>Account Information</h4>
                
                <div className="form-group">
                  <label htmlFor="acheadname">
                    Account Head Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="acheadname"
                    value={formData.acheadname}
                    onChange={(e) => setFormData({ ...formData, acheadname: e.target.value })}
                    maxLength="150"
                    placeholder="e.g., Petty Cash, Bank Account - Al Rajhi"
                    className={errors.acheadname ? 'error' : ''}
                  />
                  {errors.acheadname && (
                    <span className="error-message">{errors.acheadname}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="acgroupid">
                    Account Group <span className="required">*</span>
                  </label>
                  <select
                    id="acgroupid"
                    value={formData.acgroupid}
                    onChange={(e) => setFormData({ ...formData, acgroupid: e.target.value })}
                    className={errors.acgroupid ? 'error' : ''}
                  >
                    <option value="">-- Select Account Group --</option>
                    {groups.map((group) => (
                      <option key={group.acgroupid} value={group.acgroupid}>
                        {group.acgroupname} ({group.actype})
                      </option>
                    ))}
                  </select>
                  {errors.acgroupid && (
                    <span className="error-message">{errors.acgroupid}</span>
                  )}
                </div>
              </div>

              <div className="form-section">
                <h4>Opening Balance</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="openingbalance">Amount</label>
                    <input
                      type="number"
                      id="openingbalance"
                      value={formData.openingbalance}
                      onChange={(e) => setFormData({ ...formData, openingbalance: e.target.value })}
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className={errors.openingbalance ? 'error' : ''}
                    />
                    {errors.openingbalance && (
                      <span className="error-message">{errors.openingbalance}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="balancetype">Balance Type</label>
                    <select
                      id="balancetype"
                      value={formData.balancetype}
                      onChange={(e) => setFormData({ ...formData, balancetype: e.target.value })}
                    >
                      <option value="Dr">Debit (Dr)</option>
                      <option value="Cr">Credit (Cr)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="headorder">Display Order</label>
                  <input
                    type="number"
                    id="headorder"
                    value={formData.headorder}
                    onChange={(e) => setFormData({ ...formData, headorder: e.target.value })}
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.statushide}
                      onChange={(e) => setFormData({ ...formData, statushide: e.target.checked })}
                    />
                    <span>Hide/Inactive</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountHeads;